import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ChevronDown, Shield, GraduationCap, Calculator, DollarSign, CheckCircle2, Calendar, ArrowRight, Check, User, Search, Phone, Mail, Clock, Star, Globe, Zap, TrendingUp, Sparkles, Menu, X, HelpCircle, MapPin } from 'lucide-react';
import { trackPageView, trackCTAClick } from '../utils/analytics';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { SEO, generateFAQSchema, generateHowToBuySchema } from '../components/SEO';
import { RecentlySoldTicker, SoldTickerBanner } from '../components/RecentlySoldTicker';

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/p/Entrep%C3%B4t-Auto-Montr%C3%A9al-61577568119726/',
  linkedin: 'https://www.linkedin.com/company/entrep%C3%B4tautomontreal/'
};
import { Footer } from '../components/layout/Footer';
import { VehicleCard } from '../components/VehicleCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import GoogleRating from '../components/GoogleRating';
import { Navbar } from '../components/layout/Navbar';
import { CountUp } from '../components/CountUp';
import { PreApprovalPhone, useTypingPlaceholder } from '../components/HeroDynamic';
import { CustomerStrip } from '../components/CustomerStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const typingPlaceholder = useTypingPlaceholder(t('hero.keywordPlaceholder'));
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingArrivals, setLoadingArrivals] = useState(true);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [vehicleTab, setVehicleTab] = useState('arrivals'); // 'featured' or 'arrivals'
  
  // Search form state
  const [searchForm, setSearchForm] = useState({
    keyword: '',
    make: '',
    model: '',
    priceMin: '',
    priceMax: ''
  });

  // RDV booking form state
  const [rdvForm, setRdvForm] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    message: ''
  });
  const [rdvSubmitting, setRdvSubmitting] = useState(false);

  const handleRdvSubmit = async (e) => {
    e.preventDefault();
    setRdvSubmitting(true);
    try {
      // Use a generic vehicle_id for general appointments
      await axios.post(`${API}/appointments`, {
        name: rdvForm.name,
        email: rdvForm.email,
        phone: rdvForm.phone,
        preferred_date: rdvForm.preferred_date,
        preferred_time: rdvForm.preferred_time,
        message: rdvForm.message,
        type: 'showroom_visit'
      });
      toast.success(t('appointment.successMessage'));
      setRdvForm({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '', message: '' });
    } catch (error) {
      toast.error(t('appointment.errorMessage'));
    } finally {
      setRdvSubmitting(false);
    }
  };

  useEffect(() => {
    // Track homepage view
    trackPageView('homepage');
    
    fetchFeaturedVehicles();
    fetchNewArrivals();
    fetchMakes();
  }, []);

  const fetchFeaturedVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/featured`);
      setFeaturedVehicles(response.data);
    } catch (error) {
      console.error('Error fetching featured vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewArrivals = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/new-arrivals`);
      setNewArrivals(response.data);
      // If no new arrivals, automatically show featured vehicles
      if (response.data.length === 0) {
        setVehicleTab('featured');
      } else {
        // Always default to arrivals when we have new arrivals
        setVehicleTab('arrivals');
      }
    } catch (error) {
      console.error('Error fetching new arrivals:', error);
      // On error, default to featured
      setVehicleTab('featured');
    } finally {
      setLoadingArrivals(false);
    }
  };

  const fetchMakes = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/stats`);
      setMakes(response.data.makes || []);
    } catch (error) {
      console.error('Error fetching makes:', error);
    }
  };

  // Fetch models when make changes
  useEffect(() => {
    if (searchForm.make) {
      const fetchModels = async () => {
        try {
          const response = await axios.get(`${API}/vehicles?limit=1000`);
          const vehiclesForMake = response.data.filter(v => 
            v.make === searchForm.make && ['available', 'new_arrival'].includes(v.status)
          );
          const uniqueModels = [...new Set(vehiclesForMake.map(v => v.model).filter(Boolean))].sort();
          setModels(uniqueModels);
        } catch (error) {
          console.error('Error fetching models:', error);
        }
      };
      fetchModels();
    } else {
      setModels([]);
      setSearchForm(prev => ({ ...prev, model: '' }));
    }
  }, [searchForm.make]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchForm.keyword) params.append('search', searchForm.keyword);
    if (searchForm.make) params.append('make', searchForm.make);
    if (searchForm.priceMin) params.append('price_min', searchForm.priceMin);
    if (searchForm.priceMax) params.append('price_max', searchForm.priceMax);
    navigate(`/inventory?${params.toString()}`);
  };

  const getGuarantees = () => [
    t('guarantee.inspection'),
    t('guarantee.warranty'),
    t('guarantee.carfax'),
    t('guarantee.noFees'),
    t('guarantee.afterSales'),
  ];

  // FAQ data for schema
  const faqData = [
    { question: t('faq.q1'), answer: t('faq.a1') },
    { question: t('faq.q2'), answer: t('faq.a2') },
    { question: t('faq.q3'), answer: t('faq.a3') },
    { question: t('faq.q4'), answer: t('faq.a4') },
  ];

  return (
    <div className="min-h-screen flex flex-col" data-testid="home-page">
      <Navbar />
      <SEO 
        title="Véhicules d'occasion de qualité à Montréal"
        description="Entrepôt Auto Montréal - Concessionnaire de véhicules d'occasion de qualité à Verdun. Financement 1re, 2e et 3e chance au crédit avec 99% d'approbations. Large inventaire de voitures, VUS et camions."
        url="https://entrepotautomontreal.ca"
        keywords="voiture occasion montréal, auto usagé verdun, financement auto, crédit auto, véhicule occasion québec, concessionnaire montréal, entrepot auto, voiture pas cher montréal, SUV occasion, 2e chance crédit auto, 3e chance crédit, financement auto mauvais crédit"
        schema={generateFAQSchema(faqData)}
      />
      {/* Car Photo Background - Fixed parallax effect */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1762195340046-415140d8b1b2?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-black/75"></div>
      </div>
      
      {/* Hero Section - Full Screen with Background Image */}
      <section className="relative z-10 min-h-screen" data-testid="hero-section">
        {/* Background Car Image */}
        <div className="absolute inset-0">
          <img 
            src="https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460835/entrepot/assets/team_photo.jpg"
            alt="Car in showroom"
            className="w-full h-full object-cover"
            fetchpriority="high"
          />
          {/* Overlay for text readability */}
          <div className="absolute inset-0 bg-black/65"></div>
        </div>

        {/* Content Overlay */}
        <div className="relative z-10">
          {/* Tagline — online-only value props with icons */}
          <div className="hidden sm:flex items-center justify-center gap-3 md:gap-5 py-6 text-[11px] md:text-[12px] font-bold text-white tracking-[0.15em]">
            <span className="inline-flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-blue-300" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
              {t('hero.vehiclesInspected')}
            </span>
            <span className="text-white/40">◆</span>
            <span className="inline-flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-blue-300" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M3 13h18M5 13l3-7h8l3 7M6 17a2 2 0 100-4 2 2 0 000 4zm12 0a2 2 0 100-4 2 2 0 000 4z"/></svg>
              {t('hero.bestPrices')}
            </span>
            <span className="text-white/40">◆</span>
            <span className="inline-flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-blue-300" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 21C7 17 3 13 3 9a9 9 0 0118 0c0 4-4 8-9 12z"/><circle cx="12" cy="9" r="2.5" fill="currentColor"/></svg>
              {t('hero.convenientLocation')}
            </span>
          </div>

          {/* Google Rating Badge */}
          <div className="flex justify-center mb-4">
            <GoogleRating variant="light" animate />
          </div>

          {/* Main Headline */}
          <h1 className="text-center text-mobile-h1 sm:text-tablet-h1 md:text-[2.5rem] lg:text-[3.5rem] leading-[1.15] text-white mb-6 sm:mb-12 px-4 pt-4 sm:pt-0 font-bold shimmer-text" data-testid="home-hero-title" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('hero.destination')}<br />{t('hero.nextVehicle')}
          </h1>

          {/* Search Box + Pre-qualification Section */}
          <div className="flex flex-col lg:flex-row justify-center items-stretch gap-4 md:gap-6 px-4 md:px-12 pb-8 md:pb-12">
            
            {/* Search Box - Left */}
            <div className="backdrop-blur-[2px] rounded-2xl shadow-2xl p-4 md:p-6 w-full lg:w-[380px] border border-white/30">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="bg-[#3b82f6] p-2 rounded-lg">
                  <Search className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white drop-shadow-md shimmer-text" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="hero-search-title">
                  {t('hero.searchVehicle')}
                </h3>
              </div>
              
              <form onSubmit={handleSearch} className="space-y-4">
                {/* Keyword */}
                <div>
                  <label className="block text-xs font-semibold text-white uppercase tracking-wider mb-1.5 drop-shadow">
                    {t('hero.keyword')}
                  </label>
                  <input
                    type="text"
                    placeholder={typingPlaceholder}
                    data-testid="hero-keyword-input"
                    value={searchForm.keyword}
                    onChange={(e) => setSearchForm({...searchForm, keyword: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-white/30 bg-white/80 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/40 focus:border-[#3b82f6] transition-all"
                  />
                </div>
                
                {/* Make */}
                <div>
                  <label className="block text-xs font-semibold text-white uppercase tracking-wider mb-1.5 drop-shadow">
                    {t('hero.brand')}
                  </label>
                  <select
                    value={searchForm.make}
                    onChange={(e) => setSearchForm({...searchForm, make: e.target.value, model: ''})}
                    className="w-full px-4 py-3 rounded-lg border border-white/30 bg-white/80 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/40 focus:border-[#3b82f6] transition-all appearance-none cursor-pointer"
                  >
                    <option value="">{t('hero.allBrands')}</option>
                    {makes.map((make) => (
                      <option key={make} value={make}>{make}</option>
                    ))}
                  </select>
                </div>

                {/* Model - Only shows when make is selected */}
                {searchForm.make && models.length > 0 && (
                  <div>
                    <label className="block text-xs font-semibold text-white uppercase tracking-wider mb-1.5 drop-shadow">
                      {t('inventory.model') || 'Modèle'}
                    </label>
                    <select
                      value={searchForm.model}
                      onChange={(e) => setSearchForm({...searchForm, model: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-white/30 bg-white/80 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/40 focus:border-[#3b82f6] transition-all appearance-none cursor-pointer"
                    >
                      <option value="">{t('hero.allModels') || 'Tous les modèles'}</option>
                      {models.map((model) => (
                        <option key={model} value={model}>{model}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Budget */}
                <div>
                  <label className="block text-xs font-semibold text-white uppercase tracking-wider mb-1.5 drop-shadow">
                    {t('hero.budget')}
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      placeholder={t('form.minPrice')}
                      value={searchForm.priceMin}
                      onChange={(e) => setSearchForm({...searchForm, priceMin: e.target.value})}
                      className="w-1/2 px-4 py-3 rounded-lg border border-white/30 bg-white/80 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/40 focus:border-[#3b82f6] transition-all"
                    />
                    <input
                      type="number"
                      placeholder={t('form.maxPrice')}
                      value={searchForm.priceMax}
                      onChange={(e) => setSearchForm({...searchForm, priceMax: e.target.value})}
                      className="w-1/2 px-4 py-3 rounded-lg border border-white/30 bg-white/80 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/40 focus:border-[#3b82f6] transition-all"
                    />
                  </div>
                </div>
                
                {/* Search Button */}
                <Button 
                  type="submit"
                  className="w-full bg-[#3b82f6] hover:bg-blue-600 rounded-lg text-sm font-bold py-4 tracking-wide mt-2"
                >
                  <Search className="h-4 w-4 mr-2" />
                  {t('hero.search')}
                </Button>
              </form>
            </div>

            {/* Pre-qualification Card - Right (hidden on small mobile, shown on sm+) */}
            <div className="hidden sm:flex flex-col md:flex-row items-stretch backdrop-blur-[2px] rounded-2xl shadow-2xl overflow-hidden lg:max-w-[520px] border border-white/30">
              
              {/* Phone Section - Left */}
              <div className="bg-white/30 p-4 md:p-5 flex flex-col items-center justify-center md:w-[200px] border-b md:border-b-0 md:border-r border-white/20">
                {/* Phone Frame */}
                <PreApprovalPhone />
              </div>

              {/* Content Section - Right */}
              <div className="p-4 md:p-5 flex-1">
                <h2 className="text-[16px] md:text-[18px] leading-tight text-white mb-2 font-semibold drop-shadow-md shimmer-text" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="hero-preapproval-title">
                  {t('hero.preQualifyTitle')}
                </h2>
                
                <div className="flex items-center gap-2 text-[#60a5fa] mb-3 md:mb-4">
                  <Check className="h-4 w-4" strokeWidth={3} />
                  <span className="text-xs font-semibold">{t('hero.noImpact')}</span>
                </div>

                {/* Feature Icons Row */}
                <div className="flex justify-between gap-1 mb-3 md:mb-4">
                  <div className="text-center flex-1">
                    <div className="w-8 h-8 md:w-9 md:h-9 bg-white/80 rounded-lg flex items-center justify-center mx-auto mb-1 border border-white/30 icon-glow" style={{ animationDelay: '0ms' }}>
                      <Shield className="h-3.5 w-3.5 md:h-4 md:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-[7px] md:text-[8px] text-white/90 leading-tight font-medium drop-shadow">{t('hero.personalized')}</p>
                  </div>
                  <div className="text-center flex-1">
                    <div className="w-8 h-8 md:w-9 md:h-9 bg-white/80 rounded-lg flex items-center justify-center mx-auto mb-1 border border-white/30 icon-glow" style={{ animationDelay: '1200ms' }}>
                      <GraduationCap className="h-3.5 w-3.5 md:h-4 md:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-[7px] md:text-[8px] text-white/90 leading-tight font-medium drop-shadow">{t('hero.education')}</p>
                  </div>
                  <div className="text-center flex-1">
                    <div className="w-8 h-8 md:w-9 md:h-9 bg-white/80 rounded-lg flex items-center justify-center mx-auto mb-1 border border-white/30 icon-glow" style={{ animationDelay: '2400ms' }}>
                      <Calculator className="h-3.5 w-3.5 md:h-4 md:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-[7px] md:text-[8px] text-white/90 leading-tight font-medium drop-shadow">{t('hero.budgetTool')}</p>
                  </div>
                  <div className="text-center flex-1">
                    <div className="w-8 h-8 md:w-9 md:h-9 bg-white/80 rounded-lg flex items-center justify-center mx-auto mb-1 border border-white/30 icon-glow" style={{ animationDelay: '3600ms' }}>
                      <DollarSign className="h-3.5 w-3.5 md:h-4 md:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-[7px] md:text-[8px] text-white/90 leading-tight font-medium drop-shadow">{t('hero.buyingPower')}</p>
                  </div>
                </div>

                <Button asChild className="w-full bg-[#3b82f6] hover:bg-blue-600 rounded-lg text-xs font-bold py-3 md:py-4 tracking-wide">
                  <Link to="/credit">{t('hero.getPreQualification')}</Link>
                </Button>
              </div>
            </div>

            {/* Mobile CTA - shown only on small screens */}
            <div className="sm:hidden bg-white/20 backdrop-blur rounded-xl p-4 border border-white/30">
              <h3 className="text-white font-semibold text-center mb-3">{t('hero.needFinancing')}</h3>
              <Button asChild className="w-full bg-[#3b82f6] hover:bg-blue-600 rounded-lg text-sm font-bold py-3">
                <Link to="/credit">{t('hero.freeCreditCheck')}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Recently Sold Banner - removed by user request */}

      {/* Discover Our Process — Premium CTA Banner */}
      <section className="relative z-10 py-12 md:py-16 px-4 md:px-6" data-testid="home-process-cta-section">
        <div className="max-w-[1400px] mx-auto">
          <Link
            to="/notre-processus"
            data-testid="home-process-cta"
            className="group relative block overflow-hidden rounded-3xl border border-white/10 hover:border-blue-400/40 transition-all duration-500"
            style={{ minHeight: '320px' }}
          >
            {/* Background video */}
            <video
              src="https://res.cloudinary.com/dtqyyb6su/video/upload/w_1600,q_auto:eco,f_mp4/v1778432741/entrepot-auto/notre-processus/processus_master.mp4"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            {/* Dark cinematic overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/30" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(59,130,246,0.25)_0%,transparent_60%)]" />

            {/* Content */}
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-12 p-8 md:p-14 lg:p-16">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-blue-500/15 backdrop-blur-sm border border-blue-400/30 text-blue-300 text-[10px] md:text-xs font-bold tracking-[0.25em] uppercase">
                  <Sparkles className="h-3 w-3" />
                  {t('notreProcessus.homeBannerBadge')}
                </div>
                <h2
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-3 leading-[1.1] tracking-tight"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {t('notreProcessus.heroTitle1')}<br className="hidden md:block" />
                  <span className="bg-gradient-to-r from-blue-300 to-white bg-clip-text text-transparent"> {t('notreProcessus.heroTitle2')}</span>
                </h2>
                <p className="text-blue-100/70 text-sm md:text-base max-w-xl leading-relaxed">
                  {t('notreProcessus.homeBannerSubtitle')}
                </p>
              </div>

              <div className="flex-shrink-0">
                <span className="inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-white text-[#05080f] font-bold text-sm tracking-wide group-hover:bg-blue-400 group-hover:text-white group-hover:scale-105 shadow-2xl shadow-blue-500/20 transition-all">
                  {t('notreProcessus.homeBannerCta')}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>

            {/* Subtle corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-blue-400/30 rounded-tl-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-blue-400/30 rounded-br-3xl pointer-events-none" />
          </Link>
        </div>
      </section>

      {/* Featured Vehicles & New Arrivals - Tabbed Section */}
      <section className="relative z-10 py-12 md:py-20" data-testid="vehicles-showcase-section">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          {/* Header with Tabs */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-12">
            <div>
              <p className="text-[#3b82f6] font-semibold mb-2 md:mb-3 text-mobile-sm md:text-sm">{t('showcase.ourSelection')}</p>
              <h2 className="text-mobile-h2 md:text-desktop-h2 lg:text-[2.25rem] text-white font-semibold shimmer-text" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {t('showcase.discoverVehicles')}
              </h2>
            </div>
            
            {/* Tab Buttons */}
            <div className="flex bg-zinc-900 p-1 rounded-xl border border-zinc-800">
              <button
                onClick={() => setVehicleTab('featured')}
                className={`flex items-center gap-2 px-4 md:px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  vehicleTab === 'featured'
                    ? 'bg-zinc-800 text-[#3b82f6] shadow-md'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                <Star className={`h-4 w-4 ${vehicleTab === 'featured' ? 'fill-[#3b82f6]' : ''}`} />
                <span className="hidden sm:inline">{t('showcase.featured')}</span>
                <span className="sm:hidden">{t('showcase.featured')}</span>
                {featuredVehicles.length > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    vehicleTab === 'featured' ? 'bg-[#3b82f6]/10 text-[#3b82f6]' : 'bg-zinc-700 text-zinc-400'
                  }`}>
                    {Math.min(featuredVehicles.length, 5)}
                  </span>
                )}
              </button>
              <button
                onClick={() => setVehicleTab('arrivals')}
                className={`flex items-center gap-2 px-4 md:px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  vehicleTab === 'arrivals'
                    ? 'bg-zinc-800 text-amber-500 shadow-md'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                <Sparkles className={`h-4 w-4 ${vehicleTab === 'arrivals' ? 'text-amber-500' : ''}`} />
                <span className="hidden sm:inline">{t('showcase.newArrivals')}</span>
                <span className="sm:hidden">{t('showcase.new')}</span>
                {newArrivals.length > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    vehicleTab === 'arrivals' ? 'bg-amber-500/20 text-amber-500' : 'bg-zinc-700 text-zinc-400'
                  }`}>
                    {Math.min(newArrivals.length, 5)}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Featured Vehicles Content */}
          {vehicleTab === 'featured' && (
            <>
              {loading ? (
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="bg-zinc-900 animate-pulse rounded-xl" style={{ aspectRatio: '16/14' }} />
                  ))}
                </div>
              ) : featuredVehicles.length > 0 ? (
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {featuredVehicles.slice(0, 5).map((vehicle, index) => (
                    <VehicleCard key={vehicle.id} vehicle={vehicle} priority={index < 2} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 md:py-16 bg-zinc-900 rounded-2xl border border-zinc-800">
                  <Star className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 text-base md:text-lg">{t('showcase.noFeatured')}</p>
                  <Button asChild className="mt-4 md:mt-6 rounded-lg">
                    <Link to="/inventory">{t('showcase.browseInventory')}</Link>
                  </Button>
                </div>
              )}
            </>
          )}

          {/* New Arrivals Content */}
          {vehicleTab === 'arrivals' && (
            <>
              {loadingArrivals ? (
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="bg-zinc-900 animate-pulse rounded-xl" style={{ aspectRatio: '16/14' }} />
                  ))}
                </div>
              ) : newArrivals.length > 0 ? (
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {newArrivals.slice(0, 5).map((vehicle, index) => (
                    <div key={vehicle.id} className="relative">
                      <div className="absolute top-2 right-2 z-10 bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        {t('showcase.new')}
                      </div>
                      <VehicleCard vehicle={vehicle} priority={index < 2} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 md:py-16 bg-zinc-900 rounded-2xl border border-zinc-800">
                  <Sparkles className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 text-base md:text-lg">{t('showcase.noNewArrivals')}</p>
                  <Button asChild className="mt-4 md:mt-6 rounded-lg">
                    <Link to="/inventory">{t('showcase.browseInventory')}</Link>
                  </Button>
                </div>
              )}
            </>
          )}

          <div className="text-center mt-8 md:mt-12">
            <Button asChild variant="outline" size="lg" className="rounded-lg font-semibold px-6 md:px-8">
              <Link to="/inventory">
                {t('showcase.viewAllVehicles')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Promotional Banner - Full Width */}
      <section className="relative z-10 bg-gradient-to-r from-[#1e3a5f] via-[#1e3a5f] to-[#2d4a6f] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="inline-block bg-[#3b82f6] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                                {t('homeFinancing.specialOffer')}
              </div>
              <h3 className="text-mobile-h2 md:text-desktop-h2 lg:text-[2rem] font-bold text-white mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                                <span dangerouslySetInnerHTML={{ __html: t('financing.allChances') }} />
              </h3>
              <p className="text-[#60a5fa] text-mobile-body-lg md:text-xl font-bold">
                                <CountUp value={t('homeFinancing.approvalRate')} data-testid="home-approval-rate" />
              </p>
            </div>
            <Button asChild className="bg-white text-[#1e3a5f] hover:bg-slate-100 rounded-full px-8 py-6 text-base font-bold shadow-lg hover:shadow-xl transition-all">
              <Link to="/credit">
                                    {t('homeFinancing.takeAdvantage')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Full Width Stats */}
      <section className="relative z-10 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
              <div className="text-mobile-h2 md:text-[2.5rem] font-bold text-[#3b82f6] mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}><CountUp value="500+" data-testid="home-stat-500" /></div>
              <p className="text-zinc-400 text-mobile-sm md:text-base font-medium">{t('stats.vehiclesSold')}</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
              <div className="text-mobile-h2 md:text-[2.5rem] font-bold text-[#3b82f6] mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}><CountUp value="98%" data-testid="home-stat-98" /></div>
              <p className="text-zinc-400 text-mobile-sm md:text-base font-medium">{t('stats.satisfiedCustomers')}</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
              <div className="text-mobile-h2 md:text-[2.5rem] font-bold text-[#3b82f6] mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}><CountUp value="150+" data-testid="home-stat-150" /></div>
              <p className="text-zinc-400 text-mobile-sm md:text-base font-medium">{t('stats.inspectionPoints')}</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
              <div className="text-mobile-h2 md:text-[2.5rem] font-bold text-[#3b82f6] mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}><CountUp value="24h" data-testid="home-stat-24h" /></div>
              <p className="text-zinc-400 text-mobile-sm md:text-base font-medium">{t('stats.fastApproval')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Financial Partners Section */}
      <section className="relative z-10 py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-center text-xs md:text-sm text-zinc-400 uppercase tracking-[0.15em] mb-6 md:mb-10 font-semibold">{t('partners.title')}</p>
          <img 
            src="https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460836/entrepot/assets/financing_image.png" 
            alt="Nos partenaires financiers - Desjardins, TD Bank, Scotiabank, iA Financial Group, Eden Park, Location Accès Crédit, Santander, Iceberg Finance"
            className="w-full max-w-5xl mx-auto"
          />
        </div>
      </section>

      {/* Programme Bienvenue - Full Width Section */}
      <section className="relative z-10 py-16 md:py-24 bg-gradient-to-br from-[#0f172a]/95 via-[#1e293b]/95 to-[#0f172a]/95 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%23ffffff" fill-opacity="1" fill-rule="evenodd"/%3E%3C/svg%3E")' }}></div>
        </div>
        
        {/* Globe decoration */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-96 h-96 opacity-10 hidden lg:block">
          <Globe className="w-full h-full text-[#3b82f6]" strokeWidth={0.5} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#3b82f6]/20 text-[#60a5fa] text-xs font-bold px-4 py-2 rounded-full mb-6">
                <Globe className="h-4 w-4" />
                {t('welcome.newArrivals')}
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {t('welcome.programTitle').split(' ')[0]} <span className="text-[#60a5fa] shimmer-accent">{t('welcome.programTitle').split(' ')[1]}</span>
              </h2>
              <p className="text-xl md:text-2xl text-white/90 mb-4 font-medium">
                {t('welcome.programSubtitle')}
              </p>
              <p className="text-slate-400 text-base md:text-lg mb-8 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('welcome.programDesc') }} />

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#3b82f6]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-[#60a5fa]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{t('welcome.internalFinancing')}</p>
                    <p className="text-slate-400 text-xs">{t('welcome.internalFinancingDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#3b82f6]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="h-5 w-5 text-[#60a5fa]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{t('welcome.fastProcess')}</p>
                    <p className="text-slate-400 text-xs">{t('welcome.fastProcessDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#3b82f6]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-[#60a5fa]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{t('welcome.evolving')}</p>
                    <p className="text-slate-400 text-xs">{t('welcome.evolvingDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#3b82f6]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-[#60a5fa]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{t('welcome.inspectedVehicles')}</p>
                    <p className="text-slate-400 text-xs">{t('welcome.inspectedVehiclesDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://www.programmebienvenue.ca/crit%C3%A8res" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#3b82f6] hover:bg-blue-600 text-white font-bold px-8 py-4 rounded-full transition-all shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30"
                >
                  {t('welcome.checkEligibility')}
                  <ArrowRight className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.programmebienvenue.ca/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white/50 text-white font-semibold px-6 py-4 rounded-full transition-all"
                >
                  {t('welcome.learnMore')}
                </a>
              </div>
            </div>

            {/* Right Content - Steps Card */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 md:p-8">
              <h3 className="text-white font-bold text-xl mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {t('welcome.howItWorks')}
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#3b82f6] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    1
                  </div>
                  <div>
                    <p className="text-white font-semibold">{t('welcome.step1')}</p>
                    <p className="text-slate-400 text-sm">{t('welcome.step1Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#3b82f6] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    2
                  </div>
                  <div>
                    <p className="text-white font-semibold">{t('welcome.step2')}</p>
                    <p className="text-slate-400 text-sm">{t('welcome.step2Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#3b82f6] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    3
                  </div>
                  <div>
                    <p className="text-white font-semibold">{t('welcome.step3')}</p>
                    <p className="text-slate-400 text-sm">{t('welcome.step3Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#3b82f6] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    4
                  </div>
                  <div>
                    <p className="text-white font-semibold">{t('welcome.step4')}</p>
                    <p className="text-slate-400 text-sm">{t('welcome.step4Desc')}</p>
                  </div>
                </div>
              </div>

              {/* Testimonial */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 text-sm italic mb-3">
                  "{t('welcome.testimonial')}"
                </p>
                <p className="text-white font-semibold text-sm">— Samir, Montréal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="relative z-10 py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <p className="text-[#3b82f6] font-semibold mb-2 md:mb-3 text-mobile-sm md:text-sm">{t('transparency.title')}</p>
              <h2 className="text-mobile-h2 md:text-desktop-h2 lg:text-[2.25rem] leading-tight text-white mb-4 md:mb-5 font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                                {t('transparency.headline')} <span className="text-[#3b82f6] shimmer-accent">{t('transparency.questions')}</span>
              </h2>
              <p className="text-zinc-400 mb-6 md:mb-8 text-mobile-body md:text-lg">
                                {t('transparency.description')}
              </p>
              <div className="flex items-center gap-6">
                <div className="bg-zinc-900 rounded-lg px-4 md:px-5 py-2 md:py-3 border border-zinc-800">
                  <span className="font-bold text-zinc-300 text-base md:text-lg">CARFAX</span>
                </div>
              </div>
            </div>
            <div className="bg-[#3b82f6]/10 rounded-2xl md:rounded-3xl p-6 md:p-10 border border-[#3b82f6]/20">
              <img 
                loading="lazy" src="https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=500&h=350&fit=crop"
                alt="Price report"
                className="rounded-xl md:rounded-2xl w-full shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trade-In Banner - Full Width */}
      <section className="relative z-10 bg-[#3b82f6] overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block">
          <img 
            loading="lazy" src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=400&fit=crop"
            alt="Trade in your car"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3b82f6] to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="flex flex-col gap-8">
            {/* Header Row */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 max-w-3xl">
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {t('tradeIn.tradeOrSell')} <Link to="/vendre" className="underline underline-offset-4 hover:text-blue-200 transition-colors">{t('tradeIn.sellLink')}</Link> {t('tradeIn.yourVehicle')}
                </h3>
                <p className="text-white/90 text-sm md:text-base">
                  {t('tradeIn.description')}
                </p>
              </div>
              <Button asChild className="bg-white text-[#3b82f6] hover:bg-slate-100 rounded-full px-6 py-5 text-sm font-bold shadow-lg whitespace-nowrap">
                <Link to="/vendre">
                  <Phone className="mr-2 h-4 w-4" />
                                  {t('tradeIn.freeEvaluation')}
                </Link>
              </Button>
            </div>
            
            {/* Steps Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {/* Step 1 */}
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#3b82f6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-medium">{t('tradeIn.step1Label')}</p>
                  <p className="text-white font-semibold text-sm">{t('tradeIn.step1')}</p>
                </div>
              </div>
              
              {/* Step 2 */}
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#3b82f6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-medium">{t('tradeIn.step2Label')}</p>
                  <p className="text-white font-semibold text-sm">{t('tradeIn.step2')}</p>
                </div>
              </div>
              
              {/* Step 3 */}
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#3b82f6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-medium">{t('tradeIn.step3Label')}</p>
                  <p className="text-white font-semibold text-sm">{t('tradeIn.step3')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RDV Booking Section */}
      <section className="relative z-10 py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="relative order-2 lg:order-1 hidden md:block">
              <img 
                loading="lazy" src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&h=400&fit=crop"
                alt="Showroom"
                className="rounded-2xl md:rounded-3xl w-full shadow-xl"
              />
              <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-zinc-900 rounded-xl md:rounded-2xl p-4 md:p-6 shadow-xl border border-zinc-800">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="bg-[#3b82f6]/20 p-2 md:p-3 rounded-lg md:rounded-xl">
                    <Calendar className="h-6 w-6 md:h-8 md:w-8 text-[#3b82f6]" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm md:text-base">{t('appointment.bookVisit')}</p>
                    <p className="text-xs md:text-sm text-zinc-400">{t('appointment.schedule')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#3b82f6] font-semibold mb-2 md:mb-3 text-mobile-sm md:text-sm">{t('appointment.title')}</p>
              <h2 className="text-mobile-h2 md:text-desktop-h2 lg:text-[2.25rem] leading-tight text-white mb-4 md:mb-5 font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                                {t('appointment.visitShowroom').replace('showroom', '')} <span className="text-[#3b82f6] shimmer-accent">showroom</span>
              </h2>
              <p className="text-zinc-400 mb-4 md:mb-6 text-mobile-body md:text-lg">
                                {t('appointment.description')}
              </p>
              
              {/* RDV Form */}
              <form onSubmit={handleRdvSubmit} className="space-y-3 md:space-y-4 bg-zinc-900 rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg border border-zinc-800">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label htmlFor="rdv-name" className="text-xs md:text-sm text-zinc-400">{t('appointment.fullName')}</Label>
                    <Input
                      id="rdv-name"
                      value={rdvForm.name}
                      onChange={(e) => setRdvForm({ ...rdvForm, name: e.target.value })}
                      placeholder={t('form.namePlaceholder')}
                      required
                      className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rdv-phone" className="text-xs md:text-sm text-zinc-400">{t('appointment.phone')}</Label>
                    <Input
                      id="rdv-phone"
                      type="tel"
                      value={rdvForm.phone}
                      onChange={(e) => setRdvForm({ ...rdvForm, phone: e.target.value })}
                      placeholder="(514) 555-1234"
                      required
                      className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="rdv-email" className="text-xs md:text-sm text-zinc-400">{t('appointment.email')}</Label>
                  <Input
                    id="rdv-email"
                    type="email"
                    value={rdvForm.email}
                    onChange={(e) => setRdvForm({ ...rdvForm, email: e.target.value })}
                    placeholder="jean@exemple.com"
                    required
                    className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label htmlFor="rdv-date" className="text-xs md:text-sm text-zinc-400">{t('appointment.preferredDate')}</Label>
                    <Input
                      id="rdv-date"
                      type="date"
                      value={rdvForm.preferred_date}
                      onChange={(e) => setRdvForm({ ...rdvForm, preferred_date: e.target.value })}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rdv-time" className="text-xs md:text-sm text-zinc-400">{t('appointment.preferredTime')}</Label>
                    <select
                      id="rdv-time"
                      value={rdvForm.preferred_time}
                      onChange={(e) => setRdvForm({ ...rdvForm, preferred_time: e.target.value })}
                      required
                      className="w-full h-10 px-3 border border-zinc-700 rounded-md text-sm mt-1 bg-zinc-800 text-white"
                    >
                      <option value="">{t('form.selectTime')}</option>
                      <option value="09:00">09:00</option>
                      <option value="10:00">10:00</option>
                      <option value="11:00">11:00</option>
                      <option value="12:00">12:00</option>
                      <option value="13:00">13:00</option>
                      <option value="14:00">14:00</option>
                      <option value="15:00">15:00</option>
                      <option value="16:00">16:00</option>
                      <option value="17:00">17:00</option>
                      <option value="18:00">18:00</option>
                      <option value="19:00">19:00</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="rdv-message" className="text-xs md:text-sm text-zinc-400">{t('appointment.messageOptional')}</Label>
                  <Textarea
                    id="rdv-message"
                    value={rdvForm.message}
                    onChange={(e) => setRdvForm({ ...rdvForm, message: e.target.value })}
                    placeholder={t('form.vehiclePlaceholder')}
                    rows={2}
                    className="mt-1 resize-none bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#3b82f6] hover:bg-blue-600 rounded-lg font-semibold py-3"
                  disabled={rdvSubmitting}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                                    {rdvSubmitting ? t('appointment.submitting') : t('appointment.bookMyVisit')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="relative z-10 py-12 md:py-20 bg-slate-900/90 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <p className="text-[#3b82f6] font-semibold mb-2 md:mb-3 text-mobile-sm md:text-sm">{t('guarantee.subtitle')}</p>
              <h2 className="text-mobile-h2 md:text-desktop-h2 lg:text-[2.25rem] leading-tight mb-4 md:mb-6 font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                                {t('guarantee.title')} <span className="text-[#3b82f6] shimmer-accent">{t('guarantee.highlight')}</span>
              </h2>
              <p className="text-slate-400 mb-6 md:mb-10 text-mobile-body md:text-lg">
                                {t('guarantee.description')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5">
                {getGuarantees().map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-green-400 flex-shrink-0" />
                    <span className="text-mobile-sm md:text-sm text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              <Button asChild className="mt-6 md:mt-10 bg-[#3b82f6] hover:bg-blue-600 rounded-lg font-semibold px-5 md:px-6 py-2.5 md:py-3 text-sm md:text-base">
                <Link to="/garantie">
                  {t('guarantee.learnMore')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="relative hidden md:block">
              <img 
                loading="lazy" src="https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=600&h=400&fit=crop"
                alt="Car interior"
                className="rounded-2xl md:rounded-3xl w-full shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Financing Section */}
      <section className="relative z-10 py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="relative hidden md:block">
              <img 
                loading="lazy" src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&h=400&fit=crop"
                alt="Financement auto"
                className="rounded-2xl md:rounded-3xl w-full shadow-xl"
              />
              <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-zinc-900 rounded-xl md:rounded-2xl p-4 md:p-6 shadow-xl border border-zinc-800">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="bg-[#3b82f6]/20 p-2 md:p-3 rounded-lg md:rounded-xl">
                    <Calculator className="h-6 w-6 md:h-8 md:w-8 text-[#3b82f6]" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm md:text-base">{t('stats.fastApproval')}</p>
                    <p className="text-xs md:text-sm text-zinc-400">{t('homeFinancing.responseTime')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <p className="text-[#3b82f6] font-semibold mb-2 md:mb-3 text-mobile-sm md:text-sm">{t('homeFinancing.subtitle')}</p>
              <h2 className="text-mobile-h2 md:text-desktop-h2 lg:text-[2.25rem] leading-tight text-white mb-4 md:mb-5 font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {t('homeFinancing.title')} <span className="text-[#3b82f6] shimmer-accent">{t('homeFinancing.highlight')}</span> {t('homeFinancing.titleEnd')}
              </h2>
              <p className="text-zinc-400 mb-5 md:mb-8 text-mobile-body md:text-lg">
                {t('homeFinancing.description')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-5 md:mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-[#3b82f6] flex-shrink-0" />
                  <span className="text-mobile-sm md:text-sm text-zinc-300">{t('homeFinancing.allSituations')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-[#3b82f6] flex-shrink-0" />
                  <span className="text-mobile-sm md:text-sm text-zinc-300">{t('homeFinancing.competitiveRates')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-[#3b82f6] flex-shrink-0" />
                  <span className="text-mobile-sm md:text-sm text-zinc-300">{t('homeFinancing.fastProcess')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-[#3b82f6] flex-shrink-0" />
                  <span className="text-mobile-sm md:text-sm text-zinc-300">{t('homeFinancing.noCommitment')}</span>
                </div>
              </div>
              <Button asChild className="w-full sm:w-auto bg-[#3b82f6] hover:bg-blue-600 rounded-lg font-semibold px-5 md:px-6 py-2.5 md:py-3 text-sm md:text-base">
                <Link to="/credit">
                  {t('homeFinancing.explore')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Full Width */}
      <section className="relative z-10 py-12 md:py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-[#3b82f6] font-semibold mb-2 md:mb-3 text-xs md:text-sm">{t('testimonials.subtitle')}</p>
            <h2 className="text-2xl md:text-[40px] text-white font-semibold shimmer-text" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('testimonialsList.title')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-zinc-900 p-6 md:p-8 rounded-2xl border border-zinc-800 hover:border-zinc-700 transition-colors">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-zinc-300 mb-6 text-sm md:text-base leading-relaxed">
                "                {t('testimonialsList.review1')}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#3b82f6] rounded-full flex items-center justify-center text-white font-bold">
                  ML
                </div>
                <div>
                  <p className="font-semibold text-white">Marie L.</p>
                  <p className="text-xs text-zinc-500">Hyundai Tucson 2023</p>
                </div>
              </div>
            </div>
            {/* Testimonial 2 */}
            <div className="bg-zinc-900 p-6 md:p-8 rounded-2xl border border-zinc-800 hover:border-zinc-700 transition-colors">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-zinc-300 mb-6 text-sm md:text-base leading-relaxed">
                "                {t('testimonialsList.review2')}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#3b82f6] rounded-full flex items-center justify-center text-white font-bold">
                  PT
                </div>
                <div>
                  <p className="font-semibold text-white">Pierre T.</p>
                  <p className="text-xs text-zinc-500">Toyota RAV4 2022</p>
                </div>
              </div>
            </div>
            {/* Testimonial 3 */}
            <div className="bg-zinc-900 p-6 md:p-8 rounded-2xl border border-zinc-800 hover:border-zinc-700 transition-colors">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-zinc-300 mb-6 text-sm md:text-base leading-relaxed">
                "                {t('testimonialsList.review3')}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#3b82f6] rounded-full flex items-center justify-center text-white font-bold">
                  SB
                </div>
                <div>
                  <p className="font-semibold text-white">Sophie B.</p>
                  <p className="text-xs text-zinc-500">Honda Civic 2021</p>
                </div>
              </div>
            </div>
          </div>
          {/* Google Reviews Link */}
          <div className="text-center mt-8">
            <a href="https://share.google/3zUHE5C1OXo6MEnyL" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#3b82f6] hover:text-blue-700 font-medium text-sm">
              {t('testimonials.viewAllReviews')}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
        <CustomerStrip />
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-12 md:py-20" data-testid="cta-section">
        <div className="absolute inset-0 bg-black/80 z-0"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-[40px] text-white mb-4 md:mb-5 font-semibold shimmer-text" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('cta.title')}
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-6 md:mb-10 text-base md:text-lg">
            {t('cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#3b82f6] hover:bg-blue-600 rounded-lg font-semibold px-6 md:px-8 py-3" data-testid="cta-finance-button">
              <Link to="/inventory">
                {t('cta.browseInventory')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-lg font-semibold px-6 md:px-8 py-3 border-white text-white hover:bg-white/10">
              <Link to="/contact">
                {t('cta.speakExpert')}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section className="relative z-10 py-12 md:py-20" data-testid="faq-section" itemScope itemType="https://schema.org/FAQPage">
        <div className="absolute inset-0 bg-black/85 z-0"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-[40px] text-white font-semibold mb-4 shimmer-text" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('faq.title')}
            </h2>
            <p className="text-slate-400">{t('faq.subtitle')}</p>
          </div>
          
          <div className="space-y-4">
            {faqData.map((faq, idx) => (
            <div key={idx} className="bg-zinc-900/80 backdrop-blur-sm p-6 rounded-xl border border-zinc-800 hover:border-blue-500/50 transition-colors" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2" itemProp="name">
                <HelpCircle className="h-5 w-5 text-blue-500" />
                {faq.question}
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-slate-300" itemProp="text">
                  {faq.answer}
                </p>
              </div>
            </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recently Sold Ticker Popup - removed by user request */}

      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default HomePage;
